import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  inject,
  signal,
  NgZone,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DialogRef } from '@angular/cdk/dialog';
import { importLibrary } from '@googlemaps/js-api-loader';

import { BaseOverlayComponent, SignalButton } from '@vallift/ngl';
import {
  GoogleAddressAutoComplete
} from '@shared/components/google-address-auto-complete/google-address-auto-complete';
import { configureGoogleMaps } from '@core/config/google-maps';

export interface MapLocationValue {
  latitude: number;
  longitude: number;
  address: string;
}

@Component({
  selector: 'app-map-location-picker-overlay',
  standalone: true,
  templateUrl: './map-location-picker-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BaseOverlayComponent,
    GoogleAddressAutoComplete,
    SignalButton,
  ],
})
export class MapLocationPickerOverlay implements AfterViewInit {
  @ViewChild('mapContainer')
  private mapContainer!: ElementRef<HTMLDivElement>;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);

  private readonly dialogRef = inject(DialogRef<MapLocationValue | null>);

  private map!: google.maps.Map;

  private geocoder!: google.maps.Geocoder;

  private isBrowser = false;

  private initialized = false;

  private initializing = false;

  private geocodeTimeout?: ReturnType<typeof setTimeout>;

  private skipNextIdle = false;

  selectedLocation = signal<MapLocationValue | null>(null);

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.isBrowser = true;
    setTimeout(() => {
      this.initializeGoogleMaps();
    });
  }

  private async initializeGoogleMaps(): Promise<void> {
    if (this.initialized && this.map) {
      setTimeout(() => {
        google.maps.event.trigger(this.map, 'resize');
      });
      return;
    }
    if (this.initializing || !this.mapContainer?.nativeElement) {
      return;
    }
    this.initializing = true;
    try {
      await this.createMap();
    } finally {
      this.initializing = false;
    }
  }

  private async createMap(): Promise<void> {
    configureGoogleMaps();
    const { Map } = (await importLibrary('maps')) as google.maps.MapsLibrary;
    const { Geocoder } = (await importLibrary('geocoding')) as google.maps.GeocodingLibrary;
    const defaultPosition: google.maps.LatLngLiteral = {
      lat: 13.0827,
      lng: 80.2707,
    };
    this.map = new Map(this.mapContainer.nativeElement, {
      center: defaultPosition,
      zoom: 16,
      mapId: 'f61c3cb9bf338ceaced9e48a',
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      zoomControl: true,
      gestureHandling: 'greedy',
    });

    this.geocoder = new Geocoder();
    this.setupMapEvents();
    this.initialized = true;
    this.useCurrentLocation();
  }

  private setupMapEvents(): void {
    this.map.addListener('idle', () => {
      if (this.skipNextIdle) {
        this.skipNextIdle = false;
        return;
      }
      const center = this.map.getCenter();
      if (!center) {
        return;
      }
      const position: google.maps.LatLngLiteral = {
        lat: center.lat(),
        lng: center.lng(),
      };
      this.scheduleReverseGeocode(position);
    });
  }

  private scheduleReverseGeocode(position: google.maps.LatLngLiteral): void {
    if (this.geocodeTimeout) {
      clearTimeout(this.geocodeTimeout);
    }
    this.geocodeTimeout = setTimeout(() => {
      this.reverseGeocode(position);
    }, 400);
  }

  private reverseGeocode(position: google.maps.LatLngLiteral): void {
    if (!this.geocoder) {
      return;
    }
    this.geocoder.geocode(
      {
        location: position,
      },
      (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.length) {
          return;
        }
        const address = results[0].formatted_address;
        this.updateLocation(position, address);
      },
    );
  }

  private updateLocation(position: google.maps.LatLngLiteral, address: string): void {
    this.ngZone.run(() => {
      this.selectedLocation.set({
        latitude: position.lat,
        longitude: position.lng,
        address,
      });
    });
  }
  useCurrentLocation(): void {
    if (!this.isBrowser || !navigator.geolocation || !this.map) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location: google.maps.LatLngLiteral = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.skipNextIdle = true;
        this.map.panTo(location);
        this.map.setZoom(17);
        this.reverseGeocode(location);
      },
      (error) => {
        console.error('Unable to get current location:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }

  onLocationSelected(place: google.maps.places.Place): void {
    const location = place.location;
    if (!location || !this.map) {
      return;
    }
    const position: google.maps.LatLngLiteral = {
      lat: location.lat(),
      lng: location.lng(),
    };
    this.skipNextIdle = true;
    this.map.panTo(position);
    this.map.setZoom(17);
    this.ngZone.run(() => {
      this.selectedLocation.set({
        latitude: position.lat,
        longitude: position.lng,
        address: place.shortFormattedAddress || place.displayName || '',
      });
    });
  }

  onCancelClicked(): void {
    this.dialogRef.close(null);
  }

  onConfirmClicked(): void {
    if (!this.selectedLocation()) {
      return;
    }
    this.dialogRef.close(this.selectedLocation());
  }

}
